import { useState, useCallback, useMemo, useEffect } from 'react';
import { t } from '@lingui/core/macro';
import {
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragStartEvent,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { restrictToVerticalAxis, restrictToParentElement } from '@dnd-kit/modifiers';
import type { Folder } from '../../types/api';
import { applyOptimisticFolderOrder, useReorderFolders } from '../../services/folders';
import { useCurrentUser } from '../../services/userHooks';
import { useOptionalBookmarkDnd } from '../../contexts/BookmarkDndContext';
import SortableFolderItem from './SortableFolderItem';
import FolderItem from './FolderItem';
import FolderDragOverlay from './FolderDragOverlay';
import type { DropIndicatorPosition } from './SortableFolderItem';
import { useQueryClient } from '@tanstack/react-query';

export interface SortableFolderListProps {
  folders: Folder[];
  currentFolderId?: string;
  sortingEnabled?: boolean;
  /** True when the Folders section is collapsed — droppables stay mounted but must not accept drops. */
  dropDisabled?: boolean;
}

interface FolderDragData {
  type: 'folder-sort';
  folder: Folder;
}

/** Nested DndContext for folder sorting, independent from bookmark drag & drop */
function SortableFolderList({
  folders,
  currentFolderId,
  sortingEnabled = true,
  dropDisabled = false,
}: SortableFolderListProps) {
  const { currentUser } = useCurrentUser();
  const queryClient = useQueryClient();
  const reorderFolders = useReorderFolders();
  const bookmarkDnd = useOptionalBookmarkDnd();
  const isBookmarkBeingDragged = bookmarkDnd?.isDragging ?? false;

  const [activeId, setActiveId] = useState<string | null>(null);
  const [activeFolder, setActiveFolder] = useState<Folder | null>(null);
  const [overId, setOverId] = useState<string | null>(null);
  const [justMovedId, setJustMovedId] = useState<string | null>(null);
  // Post-drop order rendered immediately, in the same commit that clears drag
  // state. The React Query cache update propagates asynchronously (a later
  // commit), which would otherwise paint the item back at its old position
  // for a few frames. Cleared once the folders prop catches up.
  const [localOrder, setLocalOrder] = useState<string[] | null>(null);

  const resetDragState = useCallback(() => {
    setActiveId(null);
    setActiveFolder(null);
    setOverId(null);
  }, []);

  const displayedFolders = useMemo(() => {
    if (!localOrder) return folders;
    const byId = new Map(folders.map((f) => [String(f.id), f]));
    const ordered = localOrder
      .map((id) => byId.get(String(id)))
      .filter((folder): folder is Folder => Boolean(folder));
    return ordered.length === folders.length ? ordered : folders;
  }, [folders, localOrder]);

  const isSortingEnabled = sortingEnabled && folders.length > 1;
  const folderIds = useMemo(() => displayedFolders.map((f) => f.id), [displayedFolders]);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  );

  useEffect(() => {
    if (!localOrder) return;
    if (folders.map((f) => String(f.id)).join(',') === localOrder.join(',')) {
      setLocalOrder(null);
    }
  }, [folders, localOrder]);

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      const id = event.active.id as string;
      setActiveId(id);
      setActiveFolder(displayedFolders.find((f) => f.id === id) ?? null);
    },
    [displayedFolders],
  );

  const handleDragOver = useCallback((event: DragOverEvent) => {
    setOverId((event.over?.id as string) ?? null);
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (!over || active.id === over.id) {
        resetDragState();
        return;
      }

      const overData = over.data.current as FolderDragData | undefined;
      if (overData?.type !== 'folder-sort') {
        resetDragState();
        return;
      }

      const oldIndex = displayedFolders.findIndex((f) => f.id === active.id);
      const newIndex = displayedFolders.findIndex((f) => f.id === over.id);
      if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) {
        resetDragState();
        return;
      }

      const newFolderIds = arrayMove(displayedFolders, oldIndex, newIndex).map(
        (f) => f.id,
      );

      // Render the new order in the same commit that clears drag state —
      // setLocalOrder batches with resetDragState, so the dropped item never
      // paints back at its old position. The cache update below keeps every
      // other consumer of currentUser.folders consistent, but its notification
      // lands in a later commit.
      setLocalOrder(newFolderIds.map((id) => String(id)));
      applyOptimisticFolderOrder(queryClient, newFolderIds);
      setJustMovedId(active.id as string);
      setTimeout(() => setJustMovedId(null), 200);
      resetDragState();

      reorderFolders.mutate({
        folderIds: newFolderIds,
        formKey: currentUser?.formKey ?? '',
      });
    },
    [displayedFolders, reorderFolders, currentUser?.formKey, resetDragState, queryClient],
  );

  const handleDragCancel = resetDragState;

  const getDropIndicatorPosition = useCallback(
    (folderId: string): DropIndicatorPosition => {
      if (!activeId || !overId || activeId === overId || folderId !== overId) return null;

      const activeIndex = displayedFolders.findIndex((f) => f.id === activeId);
      const overIndex = displayedFolders.findIndex((f) => f.id === overId);
      if (activeIndex === -1 || overIndex === -1) return null;

      // dragging down shows below, up shows above
      return activeIndex < overIndex ? 'below' : 'above';
    },
    [activeId, overId, displayedFolders],
  );

  // Render without nested DndContext when sorting is disabled (1 folder) or when
  // a bookmark is being dragged. The nested DndContext for folder sorting captures
  // useDroppable registrations, making folder drop targets invisible to the outer
  // BookmarkDndProvider. Switching to FolderItem during bookmark drags ensures
  // drop targets register with the outer context.
  if (!isSortingEnabled || isBookmarkBeingDragged) {
    return (
      <ul id="folders" className="list-reset" aria-label={t`Folders`}>
        {folders.map((folder) => (
          <FolderItem
            key={folder.id}
            folder={folder}
            isSelected={currentFolderId === folder.id}
            dropDisabled={dropDisabled}
          />
        ))}
      </ul>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={[restrictToVerticalAxis, restrictToParentElement]}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <SortableContext items={folderIds} strategy={verticalListSortingStrategy}>
        <ul id="folders" className="list-reset" aria-label={t`Folders`}>
          {displayedFolders.map((folder) => (
            <SortableFolderItem
              key={folder.id}
              folder={folder}
              isSelected={currentFolderId === folder.id}
              dropDisabled={dropDisabled}
              dropIndicatorPosition={getDropIndicatorPosition(folder.id)}
              justMoved={folder.id === justMovedId}
              suppressTransition={justMovedId !== null && folder.id !== justMovedId}
            />
          ))}
        </ul>
      </SortableContext>
      <DragOverlay dropAnimation={null}>
        {activeFolder && <FolderDragOverlay folder={activeFolder} />}
      </DragOverlay>
    </DndContext>
  );
}

export default SortableFolderList;
